/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  useDisclosure,
  SimpleGrid,
  Spinner,
  Text,
  Icon,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  List,
  ListItem,
  Switch,
} from '@chakra-ui/react';
import { useMutation, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { toast } from 'sonner';
import { Gear, Funnel, Buildings, CheckCircle, XCircle, WarningCircle } from 'phosphor-react';
import { PageHeader } from '../../components/PageHeader';
import { IData, IDataNoPagination, ISelect } from '../../types/shared';
import { TableBox } from '../../components/tableBox';

import SelectInputForm from '../../components/inputs/forms/SelectInputForm';
import { IClient, useClients } from '../../hooks/useClients';
import { ICompany, useCompanies } from '../../hooks/useCompanies';
import { getFormattedLabelString } from '../../utils/FormatFunctions';
import { useAuth } from '../../providers/auth';
import { cleanFilters } from '../../utils/HandleFunctions';
import { CompanyDetailModal } from '../../components/CompanyDetailModal';
import { CompanyCard } from '../../components/CompanyCard';
import { SearchModeBar, SearchModeOption } from '../../components/SearchModeBar';
import { EditCharacteristicsModal } from '../../components/EditCharacteristicsModal';
import { AddFieldModal } from '../../components/AddFieldModal';
import { api } from '../../services/api';

const SEARCH_MODES: SearchModeOption[] = [
  {
    key: 'tudo',
    buttonLabel: 'Tudo',
    menuLabel: 'Tudo (ID · CNPJ · Razão)',
    placeholder: 'Buscar por ID, CNPJ ou Razão Social…',
  },
  {
    key: 'razao',
    buttonLabel: 'Razão',
    menuLabel: 'Razão social',
    placeholder: 'Digite a razão social…',
  },
  { key: 'cnpj', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Digite o CNPJ…' },
  { key: 'id', buttonLabel: 'ID', menuLabel: 'ID (external_id)', placeholder: 'Digite o ID…' },
];

export function CompaniesPage() {
  const { uploadCompanyFile, getCompaniesFilters, getCompaniesStats } = useCompanies();
  const { getClient } = useClients();
  const [searchMode, setSearchMode] = useState<'tudo' | 'razao' | 'cnpj' | 'id'>('tudo');
  const searchModeRef = useRef(searchMode);
  const { user } = useAuth();

  // Drawer de filtros
  const {
    isOpen: isFiltersDrawerOpen,
    onOpen: onFiltersDrawerOpen,
    onClose: onFiltersDrawerClose,
  } = useDisclosure();

  // Ref para o observer do scroll infinito
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Modal de detalhes da empresa
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);

  // Modal de editar características
  const {
    isOpen: isEditCharacteristicsOpen,
    onOpen: onEditCharacteristicsOpen,
    onClose: onEditCharacteristicsClose,
  } = useDisclosure();
  const [characteristicsCompany, setCharacteristicsCompany] = useState<ICompany | null>(null);

  // Modal de adicionar campos
  const {
    isOpen: isAddFieldOpen,
    onOpen: onAddFieldOpen,
    onClose: onAddFieldClose,
  } = useDisclosure();

  // Filtros por card de estatística (separados para permitir combinação)
  const [statusFilter, setStatusFilter] = useState<
    | 'all'
    | 'ativas'
    | 'processo_off'
    | 'inativas'
    | 'inativas_periodo'
    | 'fora_do_acessorias'
    | 'fora_da_dominio'
  >('ativas');
  const [showDivergencias, setShowDivergencias] = useState(false);

  // Estado para autocomplete de campos dinâmicos
  const [valorFiltroInput, setValorFiltroInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCampoDinamico, setSelectedCampoDinamico] = useState<string | null>(null);
  const [incluirCamposExcluidos, setIncluirCamposExcluidos] = useState(false);

  const handleRowClick = (company: ICompany) => {
    setSelectedCompany(company);
    onDetailOpen();
  };

  const handleEditCharacteristics = (company: ICompany) => {
    setCharacteristicsCompany(company);
    onEditCharacteristicsOpen();
  };

  // Mantém o ref atualizado
  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      size: '12',
      search: '',
    }),
  );

  const { mutateAsync: mutateAsyncUpload } = useMutation({
    mutationFn: uploadCompanyFile,
  });

  const filtersFormMethods = useForm<{
    search: string;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
    campo_filtro: ISelect<string>;
    valor_filtro: string;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
    },
  });

  const debounced = useDebounceCallback(values => {
    const searchValue = values.search?.trim() ?? '';

    // Monta os filtros removendo valores vazios (sem page para scroll infinito)
    const newFilters: Record<string, any> = {
      size: filters.get('size') ?? '12',
    };

    // Adiciona filtros dos selects apenas se tiverem valor
    if (values.cidade?.value) newFilters.cidade = values.cidade.value;
    if (values.uf?.value) newFilters.uf = values.uf.value;
    if (values.grupo_de_empresas?.value)
      newFilters.grupo_de_empresas = values.grupo_de_empresas.value;
    if (values.tags?.value) newFilters.tags = values.tags.value;
    if (values.comercial?.value) newFilters.comercial = values.comercial.value;
    if (values.contabil?.value) newFilters.contabil = values.contabil.value;
    if (values.financeiro?.value) newFilters.financeiro = values.financeiro.value;
    if (values.contas_a_pagar?.value) newFilters.contas_a_pagar = values.contas_a_pagar.value;
    if (values.encerramento_anual?.value)
      newFilters.encerramento_anual = values.encerramento_anual.value;
    if (values.fiscal?.value) newFilters.fiscal = values.fiscal.value;
    if (values.imposto_de_renda?.value) newFilters.imposto_de_renda = values.imposto_de_renda.value;
    if (values.integracao_contabil?.value)
      newFilters.integracao_contabil = values.integracao_contabil.value;
    if (values.integracao_fiscal?.value)
      newFilters.integracao_fiscal = values.integracao_fiscal.value;
    if (values.pessoal?.value) newFilters.pessoal = values.pessoal.value;
    if (values.relacionamento_com_cliente?.value)
      newFilters.relacionamento_com_cliente = values.relacionamento_com_cliente.value;
    if (values.societario?.value) newFilters.societario = values.societario.value;

    // Filtro por campos dinâmicos (formato: campo:valor)
    const campoFiltro = values.campo_filtro?.value ?? '';
    const valorFiltro = values.valor_filtro?.trim() ?? '';
    if (campoFiltro || valorFiltro) {
      newFilters.campos_filtros = `${campoFiltro}:${valorFiltro}`;
    }

    // Adiciona apenas o parâmetro de busca relevante se tiver valor
    if (searchValue) {
      const currentMode = searchModeRef.current;
      switch (currentMode) {
        case 'tudo':
          newFilters.search = searchValue;
          break;
        case 'id':
          newFilters.external_id = searchValue;
          break;
        case 'razao':
          newFilters.razao_social = searchValue;
          break;
        case 'cnpj':
          newFilters.cnpj = searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
          break;
        default:
          break;
      }
    }

    setFilters(newFilters);
  }, 500);

  // Contagem de filtros ativos (excluindo search, size e campos de busca)
  const activeFiltersCount = React.useMemo(() => {
    const filterFields = [
      'cidade',
      'uf',
      'grupo_de_empresas',
      'tags',
      'comercial',
      'contabil',
      'financeiro',
      'contas_a_pagar',
      'encerramento_anual',
      'fiscal',
      'imposto_de_renda',
      'integracao_contabil',
      'integracao_fiscal',
      'pessoal',
      'relacionamento_com_cliente',
      'societario',
      'campos_filtros',
    ];
    return filterFields.filter(field => filters.get(field)).length;
  }, [filters]);

  // Limpar todos os filtros (exceto search e size)
  const handleClearFilters = () => {
    filtersFormMethods.reset({
      search: filtersFormMethods.getValues('search'),
      cliente: null,
      cidade: null,
      uf: null,
      grupo_de_empresas: null,
      tags: null,
      comercial: null,
      contabil: null,
      financeiro: null,
      contas_a_pagar: null,
      encerramento_anual: null,
      fiscal: null,
      imposto_de_renda: null,
      integracao_contabil: null,
      integracao_fiscal: null,
      pessoal: null,
      relacionamento_com_cliente: null,
      societario: null,
      campo_filtro: null,
      valor_filtro: '',
    } as any);
    setValorFiltroInput('');
    setSelectedCampoDinamico(null);
    setIncluirCamposExcluidos(false);
    debounced(filtersFormMethods.getValues());
  };

  const queryKey = [
    'ucs',
    cleanFilters(Object.fromEntries([...Array.from(filters)])),
    statusFilter,
    showDivergencias,
  ];

  const fetchCompanies = async ({ pageParam = 1 }) => {
    const filterParams = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    // Monta os filtros de status e divergências
    const apiStatusFilter = statusFilter !== 'all' ? statusFilter : undefined;
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: {
        ...filterParams,
        page: pageParam,
        status_filter: apiStatusFilter,
        only_divergencias: showDivergencias || undefined,
      },
    });
    return res.data;
  };

  const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: fetchCompanies,
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.data.next_page,
  });

  // Combina todas as páginas em uma única lista, removendo duplicatas pelo ID
  const allCompanies = React.useMemo(() => {
    if (!data?.pages) return [];
    const seenIds = new Set<number>();
    const companies: ICompany[] = [];
    data.pages.forEach(page => {
      page.data.items.forEach(company => {
        if (!seenIds.has(company.id)) {
          seenIds.add(company.id);
          companies.push(company);
        }
      });
    });
    return companies;
  }, [data?.pages]);

  const totalCompanies = data?.pages?.[0]?.data?.total ?? 0;

  // Buscar estatísticas do backend
  const { data: statsData } = useQuery({
    queryKey: ['companiesStats'],
    queryFn: getCompaniesStats,
    staleTime: 60 * 1000, // Cache de 1 minuto
  });

  const stats = {
    total: statsData?.total ?? 0,
    ativas: statsData?.ativas ?? 0,
    ativas_periodo: statsData?.ativas_periodo ?? 0,
    processo_off: statsData?.processo_off ?? 0,
    inativas: statsData?.inativas ?? 0,
    inativas_periodo: statsData?.inativas_periodo ?? 0,
    fora_do_acessorias: statsData?.fora_do_acessorias ?? 0,
    fora_da_dominio: statsData?.fora_da_dominio ?? 0,
    divergencias: statsData?.divergencias ?? 0,
    divergencias_ativas: statsData?.divergencias_ativas ?? 0,
    divergencias_inativas: statsData?.divergencias_inativas ?? 0,
  };

  // Companies are now filtered by the backend based on statusFilter and showDivergencias
  const filteredCompanies = allCompanies;

  // Configura o IntersectionObserver para scroll infinito
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const queryKeyClient = ['clientSelected', { clientId: filters.get('cliente_id') }];

  const { data: dataClient } = useQuery<IDataNoPagination<IClient>>({
    enabled: !!filters.get('cliente_id') && !filtersFormMethods.watch('cliente'),
    queryKey: queryKeyClient,
    queryFn: getClient,
  });
  const clientSelected = dataClient?.data;

  const queryKeyCompaniesFilters = [
    'CompaniesFilters',
    { incluir_excluidos: incluirCamposExcluidos },
  ];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  // Opções para o select de campos dinâmicos
  const camposDinamicosOptions = React.useMemo(() => {
    if (!CompaniesFilters?.campos_dinamicos) return [];
    return CompaniesFilters.campos_dinamicos.map((item: { campo: string; valores: string[] }) => ({
      value: item.campo,
      label: item.campo,
    }));
  }, [CompaniesFilters?.campos_dinamicos]);

  // Valores disponíveis para autocomplete (todos os valores de todos os campos ou do campo selecionado)
  const valoresDisponiveis = React.useMemo(() => {
    if (!CompaniesFilters?.campos_dinamicos) return [];

    if (selectedCampoDinamico) {
      // Se tem campo selecionado, mostra só os valores desse campo
      const campoEncontrado = CompaniesFilters.campos_dinamicos.find(
        (item: { campo: string; valores: string[] }) => item.campo === selectedCampoDinamico,
      );
      return campoEncontrado?.valores || [];
    }

    // Se não tem campo selecionado, mostra todos os valores de todos os campos (sem duplicatas)
    const todosValores = CompaniesFilters.campos_dinamicos.flatMap(
      (item: { campo: string; valores: string[] }) => item.valores,
    );
    return Array.from(new Set(todosValores));
  }, [CompaniesFilters?.campos_dinamicos, selectedCampoDinamico]);

  // Sugestões filtradas baseadas no input do usuário
  const filteredSuggestions = React.useMemo(() => {
    // Se não tem input, só mostra sugestões se tiver um campo selecionado
    if (!valorFiltroInput.trim()) {
      return selectedCampoDinamico ? valoresDisponiveis.slice(0, 10) : [];
    }
    return valoresDisponiveis
      .filter((valor: string) => valor.toLowerCase().includes(valorFiltroInput.toLowerCase()))
      .slice(0, 10);
  }, [valoresDisponiveis, valorFiltroInput, selectedCampoDinamico]);

  useEffect(() => {
    const subscription = filtersFormMethods.watch(debounced);
    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch]);

  useEffect(() => {
    debounced(filtersFormMethods.getValues());
  }, [searchMode]);

  useEffect(() => {
    if (clientSelected) filtersFormMethods.resetField('cliente', { defaultValue: clientSelected });
  }, [clientSelected]);

  // Monitora mudanças no campo_filtro para atualizar o estado e limpar o valor
  const campoFiltroWatch = filtersFormMethods.watch('campo_filtro');
  useEffect(() => {
    const novoValor = campoFiltroWatch?.value || null;
    if (novoValor !== selectedCampoDinamico) {
      setSelectedCampoDinamico(novoValor);
      setValorFiltroInput('');
      filtersFormMethods.setValue('valor_filtro', '');
    }
  }, [campoFiltroWatch]);

  const handleFileUpload = async event => {
    const file = event.target.files;
    if (file) {
      try {
        await mutateAsyncUpload({
          file,
        });
        toast.success(`Empresas importadas com sucesso`, {
          position: 'top-center',
          duration: 2000,
        });
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ?? 'Erro interno no sistema, tente novamente mais tarde.';
        toast.error(errorMessage, { position: 'top-right', duration: 2000 });
      }
    }
    event.target.value = null;
  };
  function downloadProjectFile(fileName: string) {
    const filePath = `/${fileName}`;

    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  }

  return (
    <Box>
      <PageHeader title="Empresas" />

      {/* Cards de Estatísticas - Linha 1 */}
      <SimpleGrid
        columns={{ base: 2, sm: 3, md: 6 }}
        spacing={{ base: 2, sm: 3, md: 4 }}
        mb={{ base: 2, md: 3 }}
        px={{ base: 2, sm: 4, md: 6 }}
      >
        <Box
          bg={statusFilter === 'all' ? 'blue.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'all' ? 'lg' : 'md'}
          border={statusFilter === 'all' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'all' ? 'blue.400' : 'blue.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() => setStatusFilter(statusFilter === 'all' ? 'ativas' : 'all')}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Total de Empresas
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="blue.600"
              >
                {stats.total}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="blue.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={Buildings} boxSize={{ base: 4, sm: 5, md: 6 }} color="blue.500" />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={statusFilter === 'ativas' ? 'green.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'ativas' ? 'lg' : 'md'}
          border={statusFilter === 'ativas' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'ativas' ? 'green.400' : 'green.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() => setStatusFilter('ativas')}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Ativas
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="green.600"
              >
                {stats.ativas}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="green.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={CheckCircle}
                boxSize={{ base: 4, sm: 5, md: 6 }}
                color="green.500"
                weight="fill"
              />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={statusFilter === 'processo_off' ? 'yellow.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'processo_off' ? 'lg' : 'md'}
          border={statusFilter === 'processo_off' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'processo_off' ? 'yellow.400' : 'yellow.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() =>
            setStatusFilter(statusFilter === 'processo_off' ? 'ativas' : 'processo_off')
          }
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Emp. processo off
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="yellow.600"
              >
                {stats.processo_off}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="yellow.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={WarningCircle}
                boxSize={{ base: 4, sm: 5, md: 6 }}
                color="yellow.500"
                weight="fill"
              />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={statusFilter === 'inativas' ? 'gray.100' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'inativas' ? 'lg' : 'md'}
          border={statusFilter === 'inativas' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'inativas' ? 'gray.400' : 'gray.200'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() => setStatusFilter(statusFilter === 'inativas' ? 'ativas' : 'inativas')}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Inativas
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="gray.600"
              >
                {stats.inativas}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="gray.100"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={XCircle}
                boxSize={{ base: 4, sm: 5, md: 6 }}
                color="gray.500"
                weight="fill"
              />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={statusFilter === 'inativas_periodo' ? 'red.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'inativas_periodo' ? 'lg' : 'md'}
          border={statusFilter === 'inativas_periodo' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'inativas_periodo' ? 'red.400' : 'red.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() =>
            setStatusFilter(statusFilter === 'inativas_periodo' ? 'ativas' : 'inativas_periodo')
          }
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Inativas com Período Informado
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="red.600"
              >
                {stats.inativas_periodo}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="red.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={XCircle}
                boxSize={{ base: 4, sm: 5, md: 6 }}
                color="red.500"
                weight="fill"
              />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={showDivergencias ? 'red.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={showDivergencias ? 'lg' : 'md'}
          border={showDivergencias ? '2px solid' : '1px solid'}
          borderColor={showDivergencias ? 'red.400' : 'red.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() => setShowDivergencias(!showDivergencias)}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Divergências
              </Text>
              <Flex align="center" gap={{ base: 2, sm: 4, md: 6 }}>
                <Text
                  fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  color="red.600"
                >
                  {stats.divergencias}
                </Text>
                <Flex direction="column" gap={0}>
                  <Text fontSize={{ base: '2xs', sm: 'xs' }} color="green.600" fontWeight="medium">
                    Ativas: {stats.divergencias_ativas ?? 0}
                  </Text>
                  <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                    Inativas: {stats.divergencias_inativas ?? 0}
                  </Text>
                </Flex>
              </Flex>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="red.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={WarningCircle}
                boxSize={{ base: 4, sm: 5, md: 6 }}
                color="red.500"
                weight="fill"
              />
            </Box>
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Cards de Estatísticas - Linha 2 */}
      <SimpleGrid
        columns={{ base: 2, sm: 2, md: 2 }}
        spacing={{ base: 2, sm: 3, md: 4 }}
        mb={4}
        px={{ base: 2, sm: 4, md: 6 }}
        maxW={{ md: '40%' }}
      >
        <Box
          bg={statusFilter === 'fora_do_acessorias' ? 'orange.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'fora_do_acessorias' ? 'lg' : 'md'}
          border={statusFilter === 'fora_do_acessorias' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'fora_do_acessorias' ? 'orange.400' : 'orange.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() =>
            setStatusFilter(statusFilter === 'fora_do_acessorias' ? 'ativas' : 'fora_do_acessorias')
          }
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Fora do Acessórias
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="orange.600"
              >
                {stats.fora_do_acessorias}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="orange.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={Buildings} boxSize={{ base: 4, sm: 5, md: 6 }} color="orange.500" />
            </Box>
          </Flex>
        </Box>

        <Box
          bg={statusFilter === 'fora_da_dominio' ? 'purple.50' : 'white'}
          p={{ base: 2, sm: 3, md: 4 }}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={statusFilter === 'fora_da_dominio' ? 'lg' : 'md'}
          border={statusFilter === 'fora_da_dominio' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'fora_da_dominio' ? 'purple.400' : 'purple.100'}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          onClick={() =>
            setStatusFilter(statusFilter === 'fora_da_dominio' ? 'ativas' : 'fora_da_dominio')
          }
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize={{ base: '2xs', sm: 'xs' }} color="gray.500" fontWeight="medium">
                Fora da Domínio
              </Text>
              <Text
                fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="purple.600"
              >
                {stats.fora_da_dominio}
              </Text>
            </Box>
            <Box
              p={{ base: 1, sm: 1.5, md: 2 }}
              bg="purple.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={Buildings} boxSize={{ base: 4, sm: 5, md: 6 }} color="purple.500" />
            </Box>
          </Flex>
        </Box>
      </SimpleGrid>

      <TableBox>
        <FormProvider {...filtersFormMethods}>
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <SearchModeBar
                  modes={SEARCH_MODES}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as typeof searchMode)}
                  value={filtersFormMethods.watch('search') ?? ''}
                  onValueChange={v =>
                    filtersFormMethods.setValue('search', v, { shouldDirty: true })
                  }
                />
              </div>
              <Button
                onClick={onFiltersDrawerOpen}
                height="38px"
                width={{ base: '100%', sm: 'auto' }}
                leftIcon={<Icon as={Funnel} />}
              >
                Filtros
                {activeFiltersCount > 0 && (
                  <Badge colorScheme="blue" borderRadius="full" ml={2}>
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {user && (Number(user.grupo_id) === 1 || user.grupo_id === 1) && (
                <Button
                  colorScheme="purple"
                  size="sm"
                  height="38px"
                  leftIcon={<Icon as={Gear} weight="fill" />}
                  onClick={onAddFieldOpen}
                >
                  Gerenciar Campos
                </Button>
              )}
              <Button
                size="sm"
                height="38px"
                onClick={() => downloadProjectFile('S3D_empresas_20240814155232_60506.xlsx')}
              >
                Baixar Arq.Exemplo
              </Button>
              {user?.grupo_id !== 2 && (
                <Button as="label" htmlFor="file-upload" size="sm" height="38px" cursor="pointer">
                  Importar Empresas
                  <Input
                    type="file"
                    id="file-upload"
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                    display="none"
                  />
                </Button>
              )}
            </div>
          </div>
        </FormProvider>

        <div className="border border-b-divider" />

        {/* Grid de Cards com Scroll Infinito */}
        {isFetching && filteredCompanies.length === 0 && (
          <Box py={12} textAlign="center">
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4} color="gray.500" fontSize="lg">
              Carregando empresas...
            </Text>
          </Box>
        )}

        {!isFetching && filteredCompanies.length === 0 && (
          <Box py={12} textAlign="center">
            <Text color="gray.500" fontSize="lg">
              Nenhuma empresa encontrada.
            </Text>
          </Box>
        )}

        {filteredCompanies.length > 0 && (
          <>
            {totalCompanies > 0 && (
              <Text mt={4} mb={2} fontSize="sm" color="gray.600">
                Exibindo {filteredCompanies.length} de {totalCompanies} empresas
                {statusFilter !== 'all' && (
                  <Badge ml={2} colorScheme="blue" variant="subtle">
                    {
                      {
                        ativas: 'Ativas',
                        processo_off: 'Emp. processo off',
                        inativas: 'Inativas',
                        inativas_periodo: 'Inativas com Período Informado',
                        fora_do_acessorias: 'Fora do Acessórias',
                        fora_da_dominio: 'Fora da Domínio',
                      }[statusFilter]
                    }
                  </Badge>
                )}
                {showDivergencias && (
                  <Badge ml={2} colorScheme="red" variant="subtle">
                    Com Divergências
                  </Badge>
                )}
              </Text>
            )}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4} mt={2}>
              {filteredCompanies.map(company => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  onClick={handleRowClick}
                  onEditCharacteristics={
                    user && (Number(user.grupo_id) === 1 || user.grupo_id === 1)
                      ? handleEditCharacteristics
                      : undefined
                  }
                  showNotesButton
                  showDashboardButton
                  showDossieButton
                />
              ))}
            </SimpleGrid>

            {/* Elemento observado para carregar mais */}
            <Box ref={lastElementRef} py={4} textAlign="center">
              {isFetchingNextPage && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                  <Spinner size="md" color="blue.500" />
                  <Text color="gray.500">Carregando mais empresas...</Text>
                </Box>
              )}
              {!hasNextPage && filteredCompanies.length > 0 && (
                <Text color="gray.400" fontSize="sm">
                  Todas as empresas foram carregadas
                </Text>
              )}
            </Box>
          </>
        )}
      </TableBox>

      {/* Drawer de Filtros */}
      <Drawer
        isOpen={isFiltersDrawerOpen}
        placement="right"
        onClose={onFiltersDrawerClose}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex align="center" gap={2}>
              <Icon as={Funnel} />
              Filtros
            </Flex>
          </DrawerHeader>

          <DrawerBody pt={6}>
            <FormProvider {...filtersFormMethods}>
              <Box display="flex" flexDirection="column" gap={4}>
                <SelectInputForm<ISelect<string>>
                  name="cidade"
                  label="Cidade"
                  placeholder="Filtrar por cidade"
                  options={getFormattedLabelString(CompaniesFilters?.cidade)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="uf"
                  label="Estado"
                  placeholder="Filtrar por Estado"
                  options={getFormattedLabelString(CompaniesFilters?.uf)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="grupo_de_empresas"
                  label="Grupo de Empresas"
                  placeholder="Filtrar por grupo de empresa"
                  options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="tags"
                  label="Tags"
                  placeholder="Filtrar por tags"
                  options={getFormattedLabelString(CompaniesFilters?.tags)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="comercial"
                  label="Comercial"
                  placeholder="Filtrar por comercial"
                  options={getFormattedLabelString(CompaniesFilters?.comercial)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="contabil"
                  label="Contábil"
                  placeholder="Filtrar pelo contábil"
                  options={getFormattedLabelString(CompaniesFilters?.contabil)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="financeiro"
                  label="Financeiro"
                  placeholder="Filtrar pelo financeiro"
                  options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="contas_a_pagar"
                  label="Contas a Pagar"
                  placeholder="Filtrar por contas à pagar"
                  options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="encerramento_anual"
                  label="Encerramento Anual"
                  placeholder="Filtrar por encerramento anual"
                  options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="fiscal"
                  label="Fiscal"
                  placeholder="Filtrar por fiscal"
                  options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="imposto_de_renda"
                  label="Imposto de Renda"
                  placeholder="Filtrar por imposto de renda"
                  options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="integracao_contabil"
                  label="Integração Contábil"
                  placeholder="Filtrar por integração contábil"
                  options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="integracao_fiscal"
                  label="Integração Fiscal"
                  placeholder="Filtrar por integração fiscal"
                  options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="pessoal"
                  label="Pessoal"
                  placeholder="Filtrar por pessoal"
                  options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="relacionamento_com_cliente"
                  label="Relacionamento com Cliente"
                  placeholder="Filtrar por relacionamento com cliente"
                  options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                  controlMb={0}
                  isClearable
                />
                <SelectInputForm<ISelect<string>>
                  name="societario"
                  label="Societário"
                  placeholder="Filtrar por societário"
                  options={getFormattedLabelString(CompaniesFilters?.societario)}
                  controlMb={0}
                  isClearable
                />
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel htmlFor="incluir-excluidos" mb="0" fontSize="sm">
                    Incluir campos excluídos
                  </FormLabel>
                  <Switch
                    id="incluir-excluidos"
                    isChecked={incluirCamposExcluidos}
                    onChange={e => setIncluirCamposExcluidos(e.target.checked)}
                    colorScheme="blue"
                  />
                </FormControl>
                <SelectInputForm<ISelect<string>>
                  name="campo_filtro"
                  label="Campo Dinâmico"
                  placeholder="Selecione um campo"
                  options={camposDinamicosOptions}
                  controlMb={0}
                  isClearable
                />
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>
                    Valor do Campo
                  </FormLabel>
                  <Input
                    placeholder="Digite para buscar..."
                    value={valorFiltroInput}
                    onChange={e => {
                      setValorFiltroInput(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        filtersFormMethods.setValue('valor_filtro', valorFiltroInput);
                        setShowSuggestions(false);
                        // Aplica os filtros e fecha o drawer
                        debounced(filtersFormMethods.getValues());
                        onFiltersDrawerClose();
                      }
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  />
                  {showSuggestions && filteredSuggestions.length > 0 && (
                    <List
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      mt={1}
                      maxH="150px"
                      overflowY="auto"
                    >
                      {filteredSuggestions.map((suggestion: string) => (
                        <ListItem
                          key={suggestion}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                          onMouseDown={() => {
                            setValorFiltroInput(suggestion);
                            filtersFormMethods.setValue('valor_filtro', suggestion);
                            setShowSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </FormControl>
              </Box>
            </FormProvider>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px">
            <Button variant="outline" mr={3} onClick={handleClearFilters}>
              Limpar Filtros
            </Button>
            <Button colorScheme="blue" onClick={onFiltersDrawerClose}>
              Fechar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <CompanyDetailModal
        isOpen={isDetailOpen}
        onClose={onDetailClose}
        company={selectedCompany}
        onEditCharacteristics={
          user && (Number(user.grupo_id) === 1 || user.grupo_id === 1)
            ? handleEditCharacteristics
            : undefined
        }
        showNotesButton
        showDashboardButton
      />
      <EditCharacteristicsModal
        isOpen={isEditCharacteristicsOpen}
        onClose={onEditCharacteristicsClose}
        company={characteristicsCompany}
      />
      <AddFieldModal isOpen={isAddFieldOpen} onClose={onAddFieldClose} />
    </Box>
  );
}
